import { PathwaySettingsFormFragment$data } from "@/admin/pathways/settings/__generated__/PathwaySettingsFormFragment.graphql"
import { PathwaySettingsFormStore } from "@/admin/pathways/settings/PathwaySettingsForm"
import { ExperienceSettingsFormStore } from "@/pricing/PricingFormFields"
import { ExperienceSettingsForm_SettingsFragment$data } from "@/product/settings/__generated__/ExperienceSettingsForm_SettingsFragment.graphql"
import { ProductEntityTagInput } from "@/product/settings/ExperienceSettingsForm"
import RelayEnvironment from "@/relay/RelayEnvironment"
import { GlobalID, NodeFromConnection } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { cloneDeep } from "lodash"
import { observable } from "mobx"
import ConnectionHandler from "relay-connection-handler-plus"
import { commitLocalUpdate, RecordProxy } from "relay-runtime"
import { v4 as uuidv4 } from "uuid"

/**
 * Format the entity tags input for the mutations
 */
export function formatEntityTagsInput(
  form: ExperienceSettingsFormStore | PathwaySettingsFormStore
): {
  addEntityTags: ProductEntityTagInput[]
  removeEntityTagIds: GlobalID[]
} {
  const entityTagsDiff = ArrayUtils.diff(
    form.initialState.entityTags.map((tag) => tag.id),
    form.state.entityTags.map((tag) => tag.id)
  )
  const addEntityTags = form.state.entityTags
    .filter((tag) => entityTagsDiff.added.includes(tag.id))
    .map((t) => ({ id: t.id, name: t.name }))
  const removeEntityTagIds = entityTagsDiff.removed

  return { addEntityTags, removeEntityTagIds }
}

/**
 * Add/remove a tag in the form state
 */
export function toggleTag(
  form: ExperienceSettingsFormStore | PathwaySettingsFormStore,
  tag: ProductEntityTagInput
) {
  const index = form?.state.entityTags?.findIndex((t) => t.id === tag.id)
  if (index === -1) {
    form.state.entityTags.push(tag)
  } else {
    form.state.entityTags = observable.array(
      form.state.entityTags.filter((t) => t.id !== tag.id)
    )
  }
}

/**
 * Generate a temporary tag ID to be used in the FE
 */
export function temporaryTagId() {
  return Relay.toGlobalId("TemporaryEntityTag", uuidv4())
}

/**
 * Filter out temporary tags from the list of entity tags
 */
export function filterTemporaryTags(
  tags: ProductEntityTagInput[]
): ProductEntityTagInput[] {
  return tags.filter((tag) => {
    if (Relay.fromGlobalId(tag.id).type !== "EntityTag") {
      return false
    }

    return true
  })
}

/**
 * Invalidate the entity tags lists in the store
 */
export function invalidateTagRecords(input: { orgRecord: RecordProxy }) {
  const { orgRecord } = input

  ConnectionHandler.getConnections(orgRecord, "AdminEntityTagsTable__entityTags").forEach(
    (connection) => connection.invalidateRecord()
  )

  ConnectionHandler.getConnections(orgRecord, "AdminEntityTagsList__entityTags").forEach(
    (connection) => connection.invalidateRecord()
  )

  const entityTagsCount = (orgRecord.getValue("entityTagsCount") || 0) as number
  if (entityTagsCount > 0) orgRecord.setValue(entityTagsCount + 1, "entityTagsCount")
}

/**
 * Reset a form with entity tags from the DB, overwrite any newly created tags with the TemporaryEntityTag type
 */
export function invalidateTagForm(input: {
  form: ExperienceSettingsFormStore | PathwaySettingsFormStore
  organizationId: GlobalID
  entityTagUsages: NodeFromConnection<
    NonNullable<
      | ExperienceSettingsForm_SettingsFragment$data["entityTagUsages"]
      | PathwaySettingsFormFragment$data["entityTagUsages"]
    >
  >[]
}) {
  const { form, entityTagUsages, organizationId } = input

  // Invalidate the entity tags lists to refetch new tags
  commitLocalUpdate(RelayEnvironment, (store) => {
    const orgRecord = store.get(organizationId)
    if (!orgRecord) return
    invalidateTagRecords({ orgRecord })
  })

  // Update the form state with the new entity tags
  form.state.entityTags = observable.array(
    entityTagUsages.map((tu) => ({
      id: tu.entityTag.id,
      name: tu.entityTag.name,
    }))
  )

  // Reset the initial state of the entity tags to the new state
  form.initialState.entityTags = cloneDeep(form.state.entityTags)
}
